<script lang="ts">
const image_width = 1184
</script>
<template>
  <div
    v-if="product.name"
    class="overflow-hidden"
  >
    <cgn-breadcrumb v-for="(breadcrumb, index) in breadcrumbs" :key="index" :links="breadcrumb" />
    <div class="relative mx-auto mt-8 max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <div
        class="absolute inset-y-0 left-3/4 hidden w-screen bg-gray-300 opacity-10 dark:bg-gray-600 lg:block"
      />
      <div class="mx-auto max-w-prose text-base lg:grid lg:max-w-none lg:grid-cols-2 lg:gap-8">
        <div>
          <h1
            class="mt-2 text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-gray-100 sm:text-4xl"
          >
            {{ product.name }}
          </h1>
        </div>
      </div>
      <div class="mt-8 lg:grid lg:grid-cols-2 lg:gap-8">
        <div class="relative lg:col-start-2 lg:row-start-1">
          <svg
            class="absolute right-0 top-0 -mr-20 -mt-20 hidden lg:block"
            width="404"
            height="384"
            fill="none"
            viewBox="0 0 404 384"
            aria-hidden="true"
          >
            <defs>
              <pattern
                id="de316486-4a29-4312-bdfc-fbce2132a2c1"
                x="0"
                y="0"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x="0"
                  y="0"
                  width="4"
                  height="4"
                  class="text-gray-200 dark:text-gray-800"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect width="404" height="384" fill="url(#de316486-4a29-4312-bdfc-fbce2132a2c1)" />
          </svg>
          <div class="relative mx-auto max-w-prose text-base lg:max-w-none">
            <figure>
              <div>
                <cgn-photoswipe>
                  <div
                    v-for="(image, index) in images"
                    :key="index"
                    class="aspect-w-12 aspect-h-7 lg:aspect-none rounded-lg bg-white"
                  >
                    <a
                      :href="image.getScaledUrl(2000)"
                      data-pswp-width="2000"
                      :data-pswp-height="image.getScaledHeight(2000)"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <cgn-lazy-image
                        v-if="image_index == index"
                        class="rounded-lg object-cover object-center shadow-lg"
                        :image="image"
                        :width="image_width"
                      />
                    </a>
                  </div>
                </cgn-photoswipe>
              </div>
              <div v-if="product.photos.length > 1" class="mt-2 grid grid-cols-4 gap-4">
                <div
                  v-for="(photo, index) in product.photos"
                  :key="index"
                  class="cursor-pointer overflow-hidden rounded-lg bg-white shadow-lg transition-shadow duration-300 hover:shadow-xl"
                  @click="swapImage(index)"
                >
                  <cgn-lazy-image
                    class="rounded-lg object-cover object-center transition-opacity duration-300 hover:opacity-80"
                    :image="photo.photo"
                  />
                </div>
              </div>
            </figure>
          </div>
        </div>
        <div class="mt-8 lg:mt-0">
          <div
            class="prose-brand prose mx-auto mt-5 text-gray-500 dark:prose-dark dark:text-gray-200 lg:col-start-1 lg:row-start-1 lg:max-w-none"
            v-html="product.content"
          />
          <product-cart-options :product="product" @select-sku="swapImageToSku" />
        </div>
      </div>
      <div v-if="relatedProducts.length > 0">
        <div class="mb-4 mt-6 font-bold">
          You may also be interested in:
        </div>
        <div class="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
          <product-grid :products="relatedProducts" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { gql } from 'graphql-request'
import { SellProduct } from '~cognito/models/Sell/Product'
import { CognitoImage } from '~cognito/models/Cognito/Image'
import { $axios } from '~cognito/plugins/axios'

const props = defineProps({
  product: {
    type: String,
    required: true,
  },
})

const product = ref<SellProduct>(new SellProduct())

const image_index = ref(0)


const breadcrumbs = ref([{ name: 'Shop', url: '/shop' }])

const images = computed(() => {
  return product.value.photos?.map(e => new CognitoImage(e.photo))
})

const relatedProducts = computed(() => {
  return product.value.relateds?.map(e => e.related_product)
})

const generateBreadcrumbs = async () => {
  breadcrumbs.value = []
  let bc_groups = product.value.groups
  if (product.value.primaryGroup) {
    bc_groups = [product.value.primaryGroup]
  }

  bc_groups.forEach(async (group) => {
    const breadcrumb = ref([{ name: 'Shop', url: '/shop' }])

    group.breadcrumbs.forEach((bc) => {
      breadcrumb.value.push({ name: bc.name, url: `/shop/${bc.url}` })
    })

    breadcrumbs.value.push(breadcrumb.value)
  })
}

async function loadProduct(url: string) {
  product.value.name = ''

  const gqldata = await $axios.graphql(gql`query productByUrlQuery($url: String!, $image_width: Int!) {
  sellProduct(url: $url) {
    id
    name
    slug: url
    brand
    price
    in_stock
    groups {
      url
      name
      breadcrumbs {
        name
        url
      }
    }
    primaryGroup {
      url
      name
      breadcrumbs {
        name
        url
      }
    }
    productAddons {
      min_qty
      max_qty
      addon {
        id
        name
        type
        required
        addonDropdownOptions {
          id
          name
        }
      }
    }
    content
    blurb
    photos {
      id
      photo(image_aspect: "4x3", image_width: $image_width) {
        url
        width
        height
      }
    }
    variations {
      name
      options
    }
    skus {
      id
      sku_photo_id
      code
      skuVariations {
        name
        variation_type {
          name
        }
      }
    }
    relateds {
      related_product {
        name
        slug: url
        image(image_aspect: "4x3", image_width: 300) {
          url
          width
          height
        }
      }
    }
  }
}`, {
    url,
    image_width,
  })
  product.value = new SellProduct(gqldata.sellProduct)
  generateBreadcrumbs()
  image_index.value = 0
}

useSchemaOrg([
  defineProduct({
    // name and description can usually be inferred
    name: computed(() => product.value?.name),
    description: computed(() => product.value?.blurb),
    image: computed(() => {
      if (images.value) {
        return images.value[0].url
      }
    }),
    brand: {
      name: computed(() => product.value?.brand ? product.value.brand : 'DnD Laser Kuts'),
    },
    offers: [
      defineOffer({
        priceCurrency: 'AUD',
        price: computed(() => product.value?.price.toFixed(2)),
        availability: computed(() => product.value?.in_stock ? 'InStock' : 'OutOfStock'),
      }),
    ],
  }),
])

function swapImage(index: number) {
  image_index.value = index
}

const swapImageToSku = (sku_id: number) => {
  const sku = product.value.skus.find(e => e.id == sku_id)
  if (!sku) {
    return
  }
  if (!sku.sku_photo_id) {
    return
  }
  //  Find image index
  const img = product.value.photos.findIndex(e => e.id == sku.sku_photo_id)
  if (img == -1) {
    return
  }
  swapImage(img)
}

watch(() => props.product, (newUrl) => {
  loadProduct(newUrl)
})
onMounted(() => {
  loadProduct(props.product)
})
onServerPrefetch(async () => {
  await loadProduct(props.product)
})
</script>
